"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { AstroMascot } from "./AstroMascot";
import { 
  ArrowLeft, 
  RotateCcw, 
  Sparkles,
  Stars,
  Rocket
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface AstroOTPVerificationProps {
  onVerificationSuccess: () => void;
  onShowTerms?: () => void;
  onBack: () => void;
}

export function AstroOTPVerification({ 
  onVerificationSuccess, 
  onShowTerms, 
  onBack 
}: AstroOTPVerificationProps) {
  const [otpDigits, setOtpDigits] = React.useState<string[]>(["", "", "", "", "", ""]);
  const [currentDigitIndex, setCurrentDigitIndex] = React.useState(0);
  const [resendCount, setResendCount] = React.useState(0);
  const [countdown, setCountdown] = React.useState(0);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [attemptCount, setAttemptCount] = React.useState(0);
  const [showError, setShowError] = React.useState(false);

  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for resend
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    setShowError(false);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
      setCurrentDigitIndex(index + 1);
    }

    // Auto-verify when all digits are filled
    if (newDigits.every(digit => digit !== "") && newDigits.join("").length === 6) {
      handleVerifyOTP(newDigits.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setCurrentDigitIndex(index - 1);
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setIsVerifying(true);
    setShowError(false);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock verification logic (accept 123456 or 000000)
    if (otp === "123456" || otp === "000000") {
      setShowSuccess(true);
      setTimeout(() => {
        onVerificationSuccess();
      }, 3000);
    } else {
      setAttemptCount(prev => prev + 1);
      setShowError(true);
      setOtpDigits(["", "", "", "", "", ""]);
      setCurrentDigitIndex(0);
      inputRefs.current[0]?.focus();
    }
    
    setIsVerifying(false);
  };

  const handleResendOTP = () => {
    if (resendCount < 3 && countdown === 0) {
      setResendCount(prev => prev + 1);
      setCountdown(30);
      setOtpDigits(["", "", "", "", "", ""]);
      setCurrentDigitIndex(0);
      setShowError(false);
      inputRefs.current[0]?.focus();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Shake animation for error state
  const shakeAnimation = {
    x: [0, -5, 5, -5, 5, 0],
    transition: { duration: 0.5 }
  };

  // Confetti animation for success
  const confettiElements = Array.from({ length: 50 }, (_, i) => (
    <motion.div
      key={i}
      className="absolute w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"
      initial={{ 
        x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : Math.random() * 800, 
        y: -10,
        rotate: 0,
        scale: 0
      }}
      animate={showSuccess ? { 
        y: typeof window !== 'undefined' ? window.innerHeight + 10 : 800,
        rotate: 360,
        scale: [0, 1, 0],
        x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : Math.random() * 800
      } : {}}
      transition={{ 
        duration: 3 + Math.random() * 2,
        ease: "easeOut",
        delay: Math.random() * 2
      }}
    />
  ));

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex flex-col relative overflow-hidden">
      {/* Cosmic Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated stars */}
        <motion.div 
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: "linear" }}
        >
          {Array.from({ length: 100 }, (_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full opacity-60"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </motion.div>

        {/* Floating cosmic elements */}
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 bg-purple-400/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-40 h-40 bg-pink-400/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.6, 0.3, 0.6]
          }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-indigo-400/5 to-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Confetti Animation */}
      <AnimatePresence>
        {showSuccess && (
          <div className="fixed inset-0 pointer-events-none z-50">
            {confettiElements}
          </div>
        )}
      </AnimatePresence>

      {/* Fixed Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-black/20 backdrop-blur-sm border-b border-white/10"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-white/20" />
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto opacity-80"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-white/80">
              <Rocket className="w-5 h-5 text-purple-300" />
              <span>New Workflow - Step 1</span>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md mx-auto relative z-10 w-full">
          <AnimatePresence mode="wait">
            {!showSuccess ? (
              <motion.div
                key="otp-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center"
              >
                {/* Astro Mascot */}
                <motion.div 
                  className="mb-8"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  <AstroMascot expression="waving" size="xl" className="mx-auto" />
                </motion.div>

                {/* Greeting Message */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mb-8"
                >
                  <h1 className="text-white mb-4 text-3xl">
                    Hey there, star recruit! 🌟
                  </h1>
                  <p className="text-white/80 text-lg leading-relaxed">
                    Ready to begin your DayOne journey? Let's verify it's really you.
                  </p>
                </motion.div>

                {/* OTP Input Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Card className="bg-white/10 backdrop-blur-md border-white/20 p-8 shadow-2xl">
                    <div className="mb-6">
                      <h2 className="text-white mb-2">Enter Verification Code</h2>
                      <p className="text-white/70 text-sm">
                        We've sent a 6-digit code to your registered device
                      </p>
                    </div>

                    {/* OTP Input Fields */}
                    <div className="flex justify-center space-x-3 mb-6">
                      {otpDigits.map((digit, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ 
                            opacity: 1, 
                            scale: 1,
                            ...(showError ? shakeAnimation : {})
                          }}
                          transition={{ delay: 0.8 + index * 0.1 }}
                        >
                          <Input
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleDigitChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onFocus={() => setCurrentDigitIndex(index)}
                            className={`w-12 h-12 text-center text-lg bg-white/10 border-white/30 text-white placeholder-white/50 focus:border-purple-400 focus:ring-purple-400/50 ${
                              showError ? 'border-red-400' : ''
                            } ${
                              currentDigitIndex === index ? 'ring-2 ring-purple-400/50' : ''
                            }`}
                            disabled={isVerifying}
                          />
                        </motion.div>
                      ))}
                    </div>

                    {/* Error Message */}
                    <AnimatePresence>
                      {showError && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="mb-4 p-3 bg-red-500/20 border border-red-400/30 rounded-lg"
                        >
                          <p className="text-red-200 text-sm">
                            Invalid code. Please try again. ({3 - attemptCount} attempts remaining)
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Verification Status */}
                    {isVerifying && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mb-4 flex items-center justify-center space-x-2 text-white/80"
                      >
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="text-sm">Verifying with the stars...</span>
                      </motion.div>
                    )}

                    {/* Resend Option */}
                    <div className="text-center">
                      {countdown > 0 ? (
                        <p className="text-white/70 text-sm">
                          Resend code in {formatTime(countdown)}
                        </p>
                      ) : (
                        <Button
                          variant="ghost"
                          onClick={handleResendOTP}
                          disabled={resendCount >= 3 || isVerifying}
                          className="text-purple-300 hover:text-purple-200 hover:bg-white/10 text-sm"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Resend Code ({resendCount}/3)
                        </Button>
                      )}
                    </div>

                    {/* Instructions */}
                    <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
                      <p className="text-white/70 text-xs text-center">
                        💡 Demo tip: Use <code className="bg-white/10 px-1 rounded">123456</code> or <code className="bg-white/10 px-1 rounded">000000</code> to proceed
                      </p>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-center"
              >
                {/* Success Astro */}
                <motion.div 
                  className="mb-8"
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <AstroMascot expression="excited" size="xl" className="mx-auto" />
                </motion.div>

                {/* Success Message */}
                <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-md border-green-400/30 p-8 shadow-2xl">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full mx-auto flex items-center justify-center mb-6">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    
                    <h2 className="text-white mb-4 text-2xl">
                      Stellar! 🚀
                    </h2>
                    
                    <p className="text-white/80 mb-6 leading-relaxed">
                      Identity verified successfully! Astro is preparing your personalized onboarding journey among the stars.
                    </p>

                    <motion.div
                      className="inline-flex items-center space-x-2 text-green-300"
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <Stars className="w-5 h-5" />
                      <span className="text-sm">Launching next phase...</span>
                    </motion.div>
                  </motion.div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-white/10 bg-black/20 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-white/60">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-white/60">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-white/40 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-300 hover:text-purple-200 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}